import {
  useState,
} from 'react';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  AddCardForm,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import {
  getNamespaceSymbol,
} from 'web-utils';

import * as S from '../styled';

const namespaceSymbol = getNamespaceSymbol() || 'gymcloud';
const mapState = state => ({
  defaultCardId: coreSelectors.defaultCardIdSelector(state),
  paymentMethods: coreSelectors.getResourceMappedList(
    'paymentMethods',
  )(state),
});

interface Props {
  planId: string | number
  coupon?: string
  isClient: boolean
  subscriptionId: number | string
  onSuccess?: { (intent: any): void }
};

const Subscribe = ({
  planId,
  coupon,
  isClient,
  subscriptionId,
  onSuccess,
}: Props) => {
  const {
    defaultCardId,
    paymentMethods,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const [errorMsg, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [cardFormIsVisible, setCardFormVisible] = useState(false);

  const onSetDefaultPaymentMethod = (method: any) => {
    dispatch(
      coreActions.requestSetDefaultPaymentMethod({
        id: method.id,
      }),
    );
  };

  const handleAddCard = (res: any, actions: any) => {
    dispatch(
      coreActions.requestCreatePaymentMethod({
        body: {
          id: res.paymentMethod.id,
        },
        callbacks: {
          ...actions,
          onSuccess: () => {
            setCardFormVisible(false);
          },
        },
      }),
    );
  }

  const onSubscribe = () => {
    setError(null)
    setLoading(true)
    const setPlanSuccess = (response: any) => {
      dispatch(
        coreActions.requestFetchClientEntity({
          list: 'library',
          resourceType: 'personalWorkouts',
        }),
      );
      dispatch(
        coreActions.requestFetchClientEntity({
          list: 'library',
          resourceType: 'personalPrograms',
        }),
      );
      setLoading(false);
      onSuccess?.(response);
    };

    const action = subscriptionId
      ? coreActions.requestUpdateSubscription
      : coreActions.requestSetPlan;

    const body: Record<string, any> = {
      plan_id: planId,
    }

    if (coupon) {
      body.coupon = coupon;
    }

    dispatch(
      action({
        id: subscriptionId,
        body,
        isClient,
        callbacks: {
          onError: (e) => {
            setLoading(false);
          },
          onSuccess: setPlanSuccess,
        },
      }),
    );
  }

  return (
    <>
      <Flex width="100%" column spacing={5}>
        <Text size="16px" weight="700">
          Payment Method
        </Text>
        <Text>
          Pay with card
        </Text>
        <Flex
          column
          width="100%"
        >
          {paymentMethods.map(m => (
            <Flex
              key={m.id}
              width="100%"
              align="center"
              spacing={5}
              clickable
              onClick={() => onSetDefaultPaymentMethod(m)}
            >
              <S.CheckCircle
                isDefault={m.id === defaultCardId}
              />
              <Text
                size="13px"
                color="#8e8e8e"
              >
                ending in
                {' '}
                {m.card.last4}
              </Text>
            </Flex>
          ))}
        </Flex>
        {cardFormIsVisible ? (
          <Flex
            width="100%"
            padding="10px 0"
            column
          >
            <hr />
            <AddCardForm
              submitText="Add Card"
              onCancel={() => setCardFormVisible(false)}
              onSuccess={handleAddCard}
            />
            <hr />
          </Flex>
        ) : (
          <S.LinkButton onClick={() => setCardFormVisible(true)}>
            +Add new card
          </S.LinkButton>
        )}
      </Flex>
      {errorMsg && (
        <Flex
          width="100%"
          justify="center"
          margin="15px 0"
        >
          <Text color="#b64135">
            {errorMsg}
          </Text>
        </Flex>
      )}
      <Flex column align="center" justify="center" width="100%" margin="15px">
        <S.Subscribe
          isLoading={isLoading}
          isDisabled={paymentMethods.length == 0}
          onClick={() => {
            onSubscribe();
          }}
        >
          Subscribe
        </S.Subscribe>
      </Flex>

      {namespaceSymbol === 'ideafit' && (
        <Flex align="center" justify="center" width="100%">
          <Text
            size="14px"
            color="#343A3D"
          >
            *Note: Program Builder + is a separate subscription in addition to your IDEAfit Membership,
            and must be managed from within the program builder.
          </Text>
        </Flex>
      )}
    </>
  );
};

export default Subscribe;
